import { AssignmentOutlined, AutoAwesome, InfoOutlined } from "@mui/icons-material";
import { useMemo, useRef, useState } from "react";
import intl from "src/internationalization/intl";
import { Button, Checkbox, CircularProgress, Divider, Grid, IconWrapper, InnerSection, InvestorProfileChip, Stack, Typography } from "src/lib";
import { Accordion } from "src/lib/components/composition";
import { formatCurrencyBRL, mapPerfilToProfile } from "src/lib/transformers/ClientsOverviewInfo";
import { getResponseFromQuestion, runMessageSuggestion } from "src/services/api/chat";
import { Recommendation } from "src/services/api/commitments/types";
import { generateRecommendationProducts, mergeRankingTexts } from "src/services/api/commitments";
import { useCashRecommendationStore } from "src/store/cashRecommendationStore";
import { getStoreKey } from "src/store/cashRecommendationStore/types";
import { useDrawerStore } from "src/store/drawerStore";
import { useMessageStore } from "src/store/messageStore";
import { useModalStore } from "src/store/modalStore";
import { useCommitmentAccordeonStore } from "src/store";
import { useConfirmedRankingStore } from "src/store/confirmedRankingStore";
import { IDetailsProps } from "./types";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Radio, Tooltip } from "@mui/material";

/** Ensures a value is rendered as a string; backend may send date objects (e.g. _Date__year/month/day). */
function safeDisplay(value: unknown): string {
    if (value == null || value === "") return "-";
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (typeof value === "object" && value !== null && "_Date__year" in value) {
        const d = value as { _Date__year: number; _Date__month: number; _Date__day: number };
        const y = d._Date__year ?? 0;
        const m = String(d._Date__month ?? 0).padStart(2, "0");
        const day = String(d._Date__day ?? 0).padStart(2, "0");
        return `${y}-${m}-${day}`;
    }
    return String(value);
}

/** Builds the 3 dynamic column slots (col_1/val_1, col_2/val_2, col_3/val_3); uses "-" for null; coerces objects to string. */
const getDynamicColumns = (product: Record<string, unknown>) => [
    { title: safeDisplay(product.col_1), value: safeDisplay(product.val_1) },
    { title: safeDisplay(product.col_2), value: safeDisplay(product.val_2) },
    { title: safeDisplay(product.col_3), value: safeDisplay(product.val_3) },
];


const mapObjectToItems = (obj: Record<string, unknown>, keyPrefix = ""): Array<{ key: string; value: string }> =>
    Object.entries(obj).flatMap(([key, value]) => {
        const itemKey = keyPrefix ? `${keyPrefix}.${key}` : key;
        if (value == null || value === "") return [];
        if (Array.isArray(value)) {
            if (value.length === 0) return [];
            return [{ key: itemKey, value: JSON.stringify(value) }];
        }
        if (typeof value === "object") {
            return mapObjectToItems(value as Record<string, unknown>, itemKey);
        }
        return [{ key: itemKey, value: safeDisplay(value) }];
    });

const parseQaResponseToItems = (responseJson: string): Array<{ key: string; value: string }> => {
    try {
        const parsed = JSON.parse(responseJson);
        if (Array.isArray(parsed)) {
            return parsed.flatMap((item, idx) => {
                if (item && typeof item === "object") {
                    const obj = item as Record<string, unknown>;
                    if (obj.p && typeof obj.p === "object") {
                        const fromProduct = mapObjectToItems(obj.p as Record<string, unknown>, `item_${idx + 1}`);
                        const extra = mapObjectToItems(
                            Object.fromEntries(Object.entries(obj).filter(([key]) => key !== "p")),
                            `item_${idx + 1}`
                        );
                        return [...fromProduct, ...extra].filter((entry) => entry.value !== "-");
                    }
                    return mapObjectToItems(obj, `item_${idx + 1}`).filter((entry) => entry.value !== "-");
                }
                return [{ key: `item_${idx + 1}`, value: safeDisplay(item) }].filter((entry) => entry.value !== "-");
            });
        }

        if (parsed && typeof parsed === "object") {
            const parsedObj = parsed as Record<string, unknown>;
            // Common shape from run_qa: { p: { ...productFields }, documents: [...] }
            if (parsedObj.p && typeof parsedObj.p === "object") {
                const fromProduct = mapObjectToItems(parsedObj.p as Record<string, unknown>, "");
                const extra = mapObjectToItems(
                    Object.fromEntries(Object.entries(parsedObj).filter(([key]) => key !== "p")),
                    ""
                );
                return [...fromProduct, ...extra].filter((item) => item.value !== "-");
            }
            return mapObjectToItems(parsedObj, "").filter((item) => item.value !== "-");
        }
    } catch {
        // fallback below for non-json responses
    }

    return responseJson
        ? [{ key: "detalhes", value: responseJson }]
        : [];
};

const formatPropertyLabel = (key: string): string => key.replace(/^item_\d+\./, "");


/** Fixed column widths so every row aligns; single-line cells. */
const COLUMN_WIDTHS = {
    category: 200,
    dynamic: 140,
    suitability: 120,
} as const;
const oneLineSx = { whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" };

const DEFAULT_RANKINGS = {
    fund_products_query: "MATCH (is:InvestmentStrategy)<-[:HAS_STRATEGY]-(p:Product)-[:IS_ON_MARKET]->(m:Market {idMarket : \"FUNDOS\"})\nORDER BY p.minimumInitialInvestment DESC\nWITH is, properties(p) as p\nRETURN is, collect(p)[..10] as products",
    fixed_income_products_query: "MATCH (is:InvestmentStrategy)<-[:HAS_STRATEGY]-(p:Product)-[:IS_ON_MARKET]->(m:Market {idMarket : \"RENDA FIXA\"})\nWHERE p.dateDue is not null\nORDER BY p.dateDue DESC\nWITH is, properties(p) as p\nRETURN is, collect(p)[..10] as products",
    retirement_products_query: "MATCH (is:InvestmentStrategy)<-[:HAS_STRATEGY]-(p:Product)-[:IS_ON_MARKET]->(m:Market {idMarket : \"PREVIDENCIA\"})\nORDER BY p.minimumInitialInvestment DESC\nWITH is, properties(p) as p\nRETURN is, collect(p)[..10] as products",
    variable_income_products_query: "",
    crypto_products_query: "",
    coe_products_query: "",
};

type PropertiesCacheValue = {
    items: Array<{ key: string; value: string }>
    error: string | null
}

const Details = ({ client, accordionId, variant = "commitment", opportunities = [], ...props }: IDetailsProps) => {
    const isOpportunity = variant === "opportunity"
    const confirmedRankingByMarket = useConfirmedRankingStore((s) => s.confirmedRankingByMarket)
    const confirmedFundos = useConfirmedRankingStore((s) => s.getConfirmedRanking("FUNDOS"))
    const confirmedPrevidencia = useConfirmedRankingStore((s) => s.getConfirmedRanking("PREVIDENCIA"))
    const confirmedRendaFixa = useConfirmedRankingStore((s) => s.getConfirmedRanking("RENDA FIXA"))
    const rankings = useMemo(() => {
        const base = {
            ...DEFAULT_RANKINGS,
            fund_products_query: confirmedFundos?.cypher_query ?? DEFAULT_RANKINGS.fund_products_query,
            fixed_income_products_query:
                confirmedRendaFixa?.cypher_query ?? DEFAULT_RANKINGS.fixed_income_products_query,
            retirement_products_query:
                confirmedPrevidencia?.cypher_query ?? DEFAULT_RANKINGS.retirement_products_query,
        }
        return mergeRankingTexts(base, confirmedRankingByMarket)
    }, [
        confirmedFundos?.cypher_query,
        confirmedRendaFixa?.cypher_query,
        confirmedPrevidencia?.cypher_query,
        confirmedRankingByMarket,
    ])
    const setOpen = useModalStore((s: any) => s.setOpen)
    const openDrawer = useDrawerStore((s: any) => s.openDrawer)
    const addMessage = useMessageStore((s: any) => s.addMessage)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [reloadCount, setReloadCount] = useState(0)
    const [propertiesModalOpen, setPropertiesModalOpen] = useState(false)
    const [propertiesModalTitle, setPropertiesModalTitle] = useState("")
    const [propertiesModalItems, setPropertiesModalItems] = useState<Array<{ key: string; value: string }>>([])
    const [propertiesModalError, setPropertiesModalError] = useState<string | null>(null)
    const [propertiesModalLoading, setPropertiesModalLoading] = useState(false)
    const propertiesByProductCacheRef = useRef<Map<string, PropertiesCacheValue>>(new Map())
    const setLoading = useCashRecommendationStore((s) => s.setLoading)
    const setResult = useCashRecommendationStore((s) => s.setResult)
    const setError = useCashRecommendationStore((s) => s.setError)
    const accordions = useCommitmentAccordeonStore((s: any) => s.accordions)
    const isRecommendationOpen = useCommitmentAccordeonStore((s: any) => s.isRecommendationOpen)
    const toggleRecommendation = useCommitmentAccordeonStore((s: any) => s.toggleRecommendation)
    const setRecommendations = useCommitmentAccordeonStore((s: any) => s.setRecommendations)

    const account = client?.client?.account ?? null
    const accountStr = account != null ? String(account) : null
    const cashEntry = useCashRecommendationStore((s) => (accountStr != null ? s.byKey[getStoreKey(accountStr, "cash")] : undefined))
    const commitmentEntry = useCashRecommendationStore((s) => (accountStr != null ? s.byKey[getStoreKey(accountStr, "commitment")] : undefined))
    const [selectedOpportunityKey, setSelectedOpportunityKey] = useState<string | null>(null)
    const opportunityEntry = useCashRecommendationStore((s) =>
        accountStr != null && selectedOpportunityKey != null
            ? s.byKey[getStoreKey(accountStr, "opportunity", selectedOpportunityKey)]
            : undefined
    )
    const isCashOpportunity = client?.is_cash_opportunity && !isOpportunity
    const isCommitmentEvent = !isCashOpportunity && !isOpportunity

    const suggestionsTitle = intl("invSugestions") || "Sugestões de investimento"
    const lastRender = useMemo(() => new Date().toLocaleString("pt-BR"), [reloadCount])
    const selectedRecommendationIds = useMemo(() => {
        const entry = accordions.find((item: any) => item.id === accordionId)
        return new Set((entry?.recommendations ?? []).map((rec: any) => rec.id))
    }, [accordions, accordionId])

    const handleOpenRelatedOpportunities = () => {
        setOpen("relatedOpportunitiesModal", client.client)
    }

    const handleRefreshSuggestions = () => {
        if (accountStr == null) return
        setReloadCount((prev) => prev + 1)
        if (isCashOpportunity) {
            const payload = {
                client: accountStr,
                opportunity: "",
                event: { cliente_com_caixa: { net_worth: Number(client?.net_worth ?? 0) } },
                rankings,
            }
            setLoading(accountStr, "cash")
            generateRecommendationProducts(payload as any)
                .then((response) => setResult(accountStr, "cash", response))
                .catch((err: any) => setError(accountStr, "cash", err?.message ?? "Erro ao carregar recomendações."))
            return
        }
        if (isCommitmentEvent) {
            const payload = {
                client: accountStr,
                opportunity: "",
                event: {
                    produto_a_vencer: {
                        commitmentId: client?.produto_a_vencer?.commitmentId ?? "",
                        nameProduct: client?.produto_a_vencer?.nameProduct ?? "",
                    },
                },
                rankings,
            }
            setLoading(accountStr, "commitment")
            generateRecommendationProducts(payload as any)
                .then((response) => setResult(accountStr, "commitment", response))
                .catch((err: any) => setError(accountStr, "commitment", err?.message ?? "Erro ao carregar recomendações."))
            return
        }
        if (isOpportunity && selectedOpportunityKey != null) {
            const opportunity = opportunities.find((o: any, i: number) => String(o?.objective_id ?? i) === selectedOpportunityKey)
            const payload = {
                client: accountStr,
                opportunity: opportunity?.text ?? "",
                event: { cliente_com_caixa: { net_worth: Number(client?.net_worth ?? 0) } },
                rankings,
            }
            setLoading(accountStr, "opportunity", selectedOpportunityKey)
            generateRecommendationProducts(payload as any)
                .then((response) => setResult(accountStr, "opportunity", response, selectedOpportunityKey))
                .catch((err: any) => setError(accountStr, "opportunity", err?.message ?? "Erro ao carregar recomendações.", selectedOpportunityKey))
        }
    }

    const handleOpenInvestmentDetails = (recommendation: Recommendation) => {
        setOpen("recommendationDetailsModal", { recommendation, client: client.client })
    }

    const mapProductToRecommendation = (p: any): Recommendation => ({
        product: p.product ?? "",
        category: p.category ?? "",
        liquidity: (p.val_1 != null && p.val_1 !== "") ? safeDisplay(p.val_1) : null,
        administration_fee: p.val_3 != null && p.val_3 !== "" && (typeof p.val_3 === "number" || typeof p.val_3 === "string") ? (typeof p.val_3 === "number" ? p.val_3 : parseFloat(String(p.val_3)) || null) : null,
        product_aa: p.val_2 != null && p.val_2 !== "" && (typeof p.val_2 === "number" || typeof p.val_2 === "string") ? (typeof p.val_2 === "number" ? p.val_2 : parseFloat(String(p.val_2)) || null) : null,
        effective_return_to_month: null,
        effective_return_to_12_months: null,
        cnpj: null,
        type: "",
        minimal_application: null,
        benchmark: null,
        volume: null,
        public: null,
        effective_return_to_24_months: null,
        effective_return_to_36_months: null,
    })

    const handleOpenMessageSuggestion = async () => {
        const listForSuggestion =
            isOpportunity && opportunityEntry?.data?.response_json?.list_of_products
                ? opportunityEntry.data.response_json.list_of_products
                : isCommitmentEvent && commitmentEntry?.data?.response_json?.list_of_products
                    ? commitmentEntry.data.response_json.list_of_products
                    : isCashOpportunity && cashEntry?.data?.response_json?.list_of_products
                        ? cashEntry.data.response_json.list_of_products
                        : (client.recommendations ?? [])
        const selectedList = listForSuggestion.filter((_: any, idx: number) => selectedRecommendationIds.has(String(idx)))
        const selectedRecommendations: Recommendation[] =
            isCommitmentEvent || isCashOpportunity || isOpportunity
                ? selectedList.map((p: any) => mapProductToRecommendation(p))
                : (selectedList as Recommendation[])

        try {
            setIsLoading(true)
            const response = await runMessageSuggestion(selectedRecommendations)
            const messageText = response?.natural_language_response || ""

            if (messageText) {
                addMessage({
                    id: `message-${Date.now()}`,
                    text: messageText,
                    timestamp: new Date(),
                    answer: true,
                    jsonMessage: response, // Armazena o JSON completo da resposta
                })

                openDrawer()
            }
        } catch (err: any) {
            console.error("Erro ao buscar sugestão de mensagem:", err)
            addMessage({
                id: `message-error-${Date.now()}`,
                text: "Erro ao gerar sugestão de mensagem. Tente novamente.",
                timestamp: new Date(),
                answer: true,
                status: "error",
            })
            openDrawer()
        } finally {
            setIsLoading(false)
        }
    }

    const handleOpenPropertiesPopup = async (label: string, product: Record<string, unknown>) => {
        console.log("[Details] handleOpenPropertiesPopup:start", { label, product })
        setPropertiesModalTitle(label)
        setPropertiesModalItems([])
        setPropertiesModalError(null)
        setPropertiesModalOpen(true)
        try {
            if (!product || typeof product !== "object") throw new Error("invalid-product")
            const productName = safeDisplay(product.product)
            if (!productName || productName === "-") throw new Error("missing-product-name")
            const cacheKey = `${accountStr ?? "no-account"}::${productName}`
            const cached = propertiesByProductCacheRef.current.get(cacheKey)
            if (cached) {
                setPropertiesModalItems(cached.items)
                setPropertiesModalError(cached.error)
                console.log("[Details] handleOpenPropertiesPopup:cache-hit", { cacheKey, itemsCount: cached.items.length })
                return
            }

            setPropertiesModalLoading(true)
            const question = `Me retorne todas as informacoes do produto ${productName}`
            const qaResponse = await getResponseFromQuestion(question)
            const items = parseQaResponseToItems(qaResponse?.response_json ?? "")
            const errorMessage =
                items.length === 0
                    ? "Não encontramos valores dessas propriedades neste produto de recomendação."
                    : null
            console.log("[Details] run_qa resolved items", {
                productName,
                itemsCount: items.length,
                firstTen: items.slice(0, 10),
            })

            setPropertiesModalTitle(label)
            setPropertiesModalItems(items)
            setPropertiesModalError(errorMessage)
            propertiesByProductCacheRef.current.set(cacheKey, { items, error: errorMessage })
            console.log("[Details] handleOpenPropertiesPopup:success", { label, itemsCount: items.length })
        } catch (error) {
            console.error("[Details] handleOpenPropertiesPopup:error", {
                label,
                error,
                product,
            })
            setPropertiesModalItems([])
            setPropertiesModalError("Não foi possível carregar as propriedades.")
        } finally {
            setPropertiesModalLoading(false)
        }
    }

    const propertiesPopup = (
        <Dialog open={propertiesModalOpen} onClose={() => setPropertiesModalOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>
                <Typography variant="h4" sx={{ color: "#fff" }}>
                    {propertiesModalTitle || "Propriedades do produto"}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {propertiesModalLoading ? (
                    <Grid container justifyContent="center" sx={{ py: 2 }}>
                        <CircularProgress color="primary" size={24} />
                    </Grid>
                ) : propertiesModalError ? (
                    <Typography variant="body2" sx={{ color: "#fff" }}>{propertiesModalError}</Typography>
                ) : (
                    <Grid container direction="column" gap={1}>
                        {propertiesModalItems.map((item) => (
                            <Grid key={item.key} container justifyContent="space-between" gap={1}>
                                <Typography variant="caption" sx={{ color: "#fff" }}>{formatPropertyLabel(item.key)}</Typography>
                                <Typography variant="body2" sx={{ color: "#fff", textAlign: "right", wordBreak: "break-word" }}>{item.value}</Typography>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    )

    if (isOpportunity) {
        return (
            <Accordion.Details {...props}>
                <Stack sx={{ mt: 2 }}>
                    {opportunities.map((opportunity: any, idx: number) => {
                        const key = String(opportunity?.objective_id ?? idx)
                        const isSelected = selectedOpportunityKey === key
                        return (
                            <Grid key={`opportunity-item-${key}`} sx={{ mt: 2 }}>
                                <Grid container alignItems="center" gap={4}>
                                    <Grid>
                                        <Grid container gap={1} alignItems="center">
                                            <Radio
                                                checked={isSelected}
                                                onChange={() => {
                                                    if (accountStr == null) return
                                                    setSelectedOpportunityKey(key)
                                                    setRecommendations(accordionId, [])
                                                    const payload = {
                                                        client: accountStr,
                                                        opportunity: opportunity?.text ?? "",
                                                        event: { cliente_com_caixa: { net_worth: Number(client?.net_worth ?? 0) } },
                                                        rankings,
                                                    }
                                                    setLoading(accountStr, "opportunity", key)
                                                    generateRecommendationProducts(payload as any)
                                                        .then((response: any) => setResult(accountStr, "opportunity", response, key))
                                                        .catch((err: any) => setError(accountStr, "opportunity", err?.message ?? "Erro ao carregar recomendações.", key))
                                                }}
                                            />
                                            <Typography>
                                                {opportunity?.title ?? `Oportunidade ${idx + 1}`}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid sx={{ width: 320, minWidth: 320 }}>
                                        <InnerSection title="Resumo">
                                            <Tooltip title={opportunity?.text || "-"} arrow placement="top">
                                                <span style={{ display: "block" }}>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {opportunity?.text || "-"}
                                                    </Typography>
                                                </span>
                                            </Tooltip>
                                        </InnerSection>
                                    </Grid>
                                    <Grid>
                                        <InnerSection title="Valor da oportunidade">
                                            <Typography variant="body1">
                                                {formatCurrencyBRL(opportunity?.volumeOportunidade ?? 0)}
                                            </Typography>
                                        </InnerSection>
                                    </Grid>
                                </Grid>
                                <Divider color="secondary" sx={{ mt: 2 }} />
                            </Grid>
                        )
                    })}
                </Stack>
                <Typography variant="caption" sx={{ mt: 3, display: "block", ml: 3 }}>
                    Sugestões de investimento para a oportunidade selecionada
                </Typography>
                <Grid
                    container
                    justifyContent="space-between"
                    sx={{ mt: 2, px: 2 }}
                >
                    <Typography variant="caption">
                        {suggestionsTitle}
                    </Typography>
                    <Grid container gap={2} justifyContent="flex-end" alignItems="center">
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleRefreshSuggestions}
                        >
                            Atualizar sugestões
                        </Button>
                        <Typography variant="caption">
                            {`Última atualização: ${lastRender}`}
                        </Typography>
                    </Grid>
                </Grid>
                <Stack sx={{ mt: 2, px: 2, pb: 1 }}>
                    {opportunityEntry?.loading && (
                        <Grid container justifyContent="center" sx={{ py: 3 }}>
                            <CircularProgress color="primary" />
                        </Grid>
                    )}
                    {opportunityEntry?.error && !opportunityEntry?.loading && (
                        <Typography variant="body2" sx={{ py: 2, color: (theme: any) => theme.palette?.accentError?.main ?? theme.palette?.error?.main ?? '#d32f2f' }}>
                            {opportunityEntry.error}
                        </Typography>
                    )}
                    {!opportunityEntry?.loading &&
                        opportunityEntry?.data?.response_json?.list_of_products?.map((product: any, idx: number) => (
                            <Grid key={`opportunity-rec-${idx}`} sx={{ mt: 2 }}>
                                <Grid container wrap="nowrap" alignItems="flex-start" gap={2} sx={{ minWidth: 0 }}>
                                    <Grid item sx={{ minWidth: 0, flex: "1 1 200px" }}>
                                        <Grid container wrap="nowrap" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                                            <Checkbox
                                                checked={isRecommendationOpen(accordionId, String(idx))}
                                                onChange={() => toggleRecommendation(accordionId, String(idx))}
                                            />
                                            <Typography sx={{ flexShrink: 0 }}>{`${intl('investment')} ${idx + 1}`}</Typography>
                                            <Typography variant="body1" sx={{ ...oneLineSx, flex: "1 1 0" }}>{product.product}</Typography>
                                            <Tooltip title="Ver informações do produto" arrow placement="top">
                                                <IconButton
                                                    color="secondary"
                                                    size="small"
                                                    onClick={() => handleOpenPropertiesPopup(`${intl('investment')} ${idx + 1}`, product)}
                                                >
                                                    <InfoOutlined fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                    <Grid item sx={{ flex: `0 0 ${COLUMN_WIDTHS.category}px` }}>
                                        <Typography variant="caption" display="block">{intl('category')}</Typography>
                                        <Typography variant="body1" sx={oneLineSx}>{product.category}</Typography>
                                    </Grid>
                                    {getDynamicColumns(product).map((col, i) => (
                                        <Grid item key={i} sx={{ flex: `0 0 ${COLUMN_WIDTHS.dynamic}px` }}>
                                            <Typography variant="caption" display="block">{col.title}</Typography>
                                            <Typography variant="body1" sx={oneLineSx}>{col.value}</Typography>
                                        </Grid>
                                    ))}
                                    <Grid item sx={{ flex: `0 0 ${COLUMN_WIDTHS.suitability}px` }}>
                                        <Typography variant="caption" display="block">{intl('suitability')}</Typography>
                                        <Box sx={oneLineSx}><InvestorProfileChip profile={mapPerfilToProfile(product.suitability || client.client.perfil)} /></Box>
                                    </Grid>
                                </Grid>
                                <Divider color="secondary" sx={{ mt: 2 }} />
                            </Grid>
                        ))}
                </Stack>
                <Grid
                    container
                    sx={{ width: "100%" }}
                    justifyContent="flex-end"
                >
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2 }}
                        disabled={selectedRecommendationIds.size === 0 || isLoading}
                        onClick={handleOpenMessageSuggestion}
                    >
                        <Grid container gap={2}>
                            <IconWrapper color="neutral">
                                <AutoAwesome />
                            </IconWrapper>
                            <Typography>
                                {intl('sugestInvestimentMsg')}
                            </Typography>
                        </Grid>
                    </Button>
                </Grid>
                {propertiesPopup}
            </Accordion.Details>
        )
    }

    return (
        <Accordion.Details {...props}>
            <Grid
                container
                justifyContent="space-between"
                sx={{ mt: 3 }}
            >
                <Typography variant="caption">
                    {suggestionsTitle}
                </Typography>
                <Grid>
                    <Grid container gap={2} alignItems="center">
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleRefreshSuggestions}
                        >
                            Atualizar sugestões
                        </Button>
                        <Typography variant="caption">
                            {`Última atualização: ${lastRender}`}
                        </Typography>
                        {client?.opportunitiesCount > 0 && (
                            <Button
                                variant="outlined"
                                color="neutral"
                                onClick={handleOpenRelatedOpportunities}
                            >
                                <Grid
                                    container
                                    gap={1}
                                    alignItems="center"
                                >
                                    <IconWrapper color="neutral">
                                        <AssignmentOutlined />
                                    </IconWrapper>
                                    <Typography variant="caption">
                                        {`${client.opportunitiesCount} ${client.opportunitiesCount > 1 ? intl('opportunitiesRelatedTo') : intl('opportunityRelatedTo')} ${client.client.name}`}
                                    </Typography>
                                </Grid>
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <Stack sx={{ mt: 2 }}>
                {isCommitmentEvent && commitmentEntry?.loading && (
                    <Grid container justifyContent="center" sx={{ py: 3 }}>
                        <CircularProgress color="primary" />
                    </Grid>
                )}
                {isCommitmentEvent && commitmentEntry?.error && !commitmentEntry?.loading && (
                    <Typography variant="body2" sx={{ py: 2, color: (theme: any) => theme.palette?.accentError?.main ?? theme.palette?.error?.main ?? '#d32f2f' }}>
                        {commitmentEntry.error}
                    </Typography>
                )}
                {isCommitmentEvent &&
                    !commitmentEntry?.loading &&
                    commitmentEntry?.data?.response_json?.list_of_products?.map((product: any, idx: number) => (
                        <Grid key={`commitment-rec-${idx}`} sx={{ mt: 2 }}>
                            <Grid container wrap="nowrap" alignItems="flex-start" gap={2} sx={{ minWidth: 0 }}>
                                <Grid item sx={{ minWidth: 0, flex: "1 1 200px" }}>
                                    <Grid container wrap="nowrap" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                                        <Checkbox checked={isRecommendationOpen(accordionId, String(idx))} onChange={() => toggleRecommendation(accordionId, String(idx))} />
                                        <Typography sx={{ flexShrink: 0 }}>{`${intl('investment')} ${idx + 1}`}</Typography>
                                        <Typography variant="body1" sx={{ ...oneLineSx, flex: "1 1 0" }}>{product.product}</Typography>
                                        <Tooltip title="Ver informações do produto" arrow placement="top">
                                            <IconButton
                                                color="secondary"
                                                size="small"
                                                onClick={() => handleOpenPropertiesPopup(`${intl('investment')} ${idx + 1}`, product)}
                                            >
                                                <InfoOutlined fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                                <Grid item sx={{ flex: `0 0 ${COLUMN_WIDTHS.category}px` }}>
                                    <Typography variant="caption" display="block">{intl('category')}</Typography>
                                    <Typography variant="body1" sx={oneLineSx}>{product.category}</Typography>
                                </Grid>
                                {getDynamicColumns(product).map((col, i) => (
                                    <Grid item key={i} sx={{ flex: `0 0 ${COLUMN_WIDTHS.dynamic}px` }}>
                                        <Typography variant="caption" display="block">{col.title}</Typography>
                                        <Typography variant="body1" sx={oneLineSx}>{col.value}</Typography>
                                    </Grid>
                                ))}
                                <Grid item sx={{ flex: `0 0 ${COLUMN_WIDTHS.suitability}px` }}>
                                    <Typography variant="caption" display="block">{intl('suitability')}</Typography>
                                    <Box sx={oneLineSx}><InvestorProfileChip profile={mapPerfilToProfile(product.suitability || client.client.perfil)} /></Box>
                                </Grid>
                            </Grid>
                            <Divider color="secondary" sx={{ mt: 2 }} />
                        </Grid>
                    ))}
                {isCashOpportunity && cashEntry?.loading && (
                    <Grid container justifyContent="center" sx={{ py: 3 }}>
                        <CircularProgress color="primary" />
                    </Grid>
                )}
                {isCashOpportunity && cashEntry?.error && !cashEntry?.loading && (
                    <Typography variant="body2" sx={{ py: 2, color: (theme: any) => theme.palette?.accentError?.main ?? theme.palette?.error?.main ?? '#d32f2f' }}>
                        {cashEntry.error}
                    </Typography>
                )}
                {isCashOpportunity &&
                    !cashEntry?.loading &&
                    cashEntry?.data?.response_json?.list_of_products?.map((product: any, idx: number) => (
                        <Grid key={`cash-rec-${idx}`} sx={{ mt: 2 }}>
                            <Grid container wrap="nowrap" alignItems="flex-start" gap={2} sx={{ minWidth: 0 }}>
                                <Grid item sx={{ minWidth: 0, flex: "1 1 200px" }}>
                                    <Grid container wrap="nowrap" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                                        <Checkbox
                                            checked={isRecommendationOpen(accordionId, String(idx))}
                                            onChange={() => toggleRecommendation(accordionId, String(idx))}
                                        />
                                        <Typography sx={{ flexShrink: 0 }}>{`${intl('investment')} ${idx + 1}`}</Typography>
                                        <Typography variant="body1" sx={{ ...oneLineSx, flex: "1 1 0" }}>{product.product}</Typography>
                                        <Tooltip title="Ver informações do produto" arrow placement="top">
                                            <IconButton
                                                color="secondary"
                                                size="small"
                                                onClick={() => handleOpenPropertiesPopup(`${intl('investment')} ${idx + 1}`, product)}
                                            >
                                                <InfoOutlined fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                                <Grid item sx={{ flex: `0 0 ${COLUMN_WIDTHS.category}px` }}>
                                    <Typography variant="caption" display="block">{intl('category')}</Typography>
                                    <Typography variant="body1" sx={oneLineSx}>{product.category}</Typography>
                                </Grid>
                                {getDynamicColumns(product).map((col, i) => (
                                    <Grid item key={i} sx={{ flex: `0 0 ${COLUMN_WIDTHS.dynamic}px` }}>
                                        <Typography variant="caption" display="block">{col.title}</Typography>
                                        <Typography variant="body1" sx={oneLineSx}>{col.value}</Typography>
                                    </Grid>
                                ))}
                                <Grid item sx={{ flex: `0 0 ${COLUMN_WIDTHS.suitability}px` }}>
                                    <Typography variant="caption" display="block">{intl('suitability')}</Typography>
                                    <Box sx={oneLineSx}><InvestorProfileChip profile={mapPerfilToProfile(product.suitability || client.client.perfil)} /></Box>
                                </Grid>
                            </Grid>
                            <Divider color="secondary" sx={{ mt: 2 }} />
                        </Grid>
                    ))}
            </Stack>
            <Grid
                container
                sx={{ width: "100%" }}
                justifyContent="flex-end"
            >
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    disabled={selectedRecommendationIds.size === 0 || isLoading}
                    onClick={handleOpenMessageSuggestion}
                >
                    <Grid container gap={2}>
                        <IconWrapper color="neutral">
                            <AutoAwesome />
                        </IconWrapper>
                        <Typography>
                            {intl('sugestInvestimentMsg')}
                        </Typography>
                    </Grid>
                </Button>
            </Grid>
            {propertiesPopup}
        </Accordion.Details>
    )
}

export { Details };

